import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { safePostCall } from 'helpers/firebaseSetup';
import endpoints from 'constants/endpoints';
import { driver } from 'driver.js';
import { TOUR_SECTIONS } from './tourConfig';
import { USER_TYPES } from 'constants/enum';
import { getCompany, getFrontendInstance } from 'helpers/getCompanyVariables';
import { isAgentRankLimited } from 'helpers/agents';
import {
  getCarrierTypeOptions,
  getParentGroupLabel
} from 'helpers/carriers/carriers';
import { isViewingAsAgent } from 'helpers/viewAsAgentLink';
import { setCompletedTourSections } from 'state/actions/user';
import useViewAs from 'hooks/useViewAs';

const company = getCompany();
const isOneill = company === 'oneill';

const PortalWalkthroughContext = createContext(null);

export function usePortalWalkthrough() {
  return useContext(PortalWalkthroughContext);
}

function completedIdsEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length)
    return false;
  return a.every((id, i) => id === b[i]);
}

function shouldSyncTourSectionToZoho(
  user,
  sectionId,
  completedIdsBeforeAdvance
) {
  // recordTourSection is only registered as a Zoho endpoint for O'Neill
  if (!isOneill) return false;
  if (!sectionId || sectionId === 'checklist') return false;
  if (!user?.id) return false;
  if (completedIdsBeforeAdvance.includes(sectionId)) return false;
  const zohoSections = user.completedTourSections;
  if (Array.isArray(zohoSections) && zohoSections.includes(sectionId)) {
    return false;
  }
  return true;
}

// SafePortalProxy puts agentId/sectionId in crmAPIRequest.body as a JSON *string* (this is expected).
// Zoho Deluge must use bodyRaw.toMap() — see companyScriptFolder/zoho-deluge/portal_tour_section_record.deluge
function parseZohoFunctionOutput(res) {
  const output = res?.data?.details?.output;
  if (output == null || output === '') return null;
  if (typeof output === 'object') return output;
  try {
    return JSON.parse(output);
  } catch {
    return { ok: true, message: output };
  }
}

function recordTourSectionToZoho(agentId, sectionId, sectionName) {
  const normalizedAgentId = agentId != null ? String(agentId) : '';
  if (!normalizedAgentId || !sectionId) {
    console.warn(
      '[tour] recordTourSection skipped — missing agentId or sectionId',
      {
        agentId: normalizedAgentId,
        sectionId
      }
    );
    return Promise.resolve(null);
  }

  return safePostCall(endpoints.awsProxy.safePost, {
    isZohoRequest: true,
    endpoint: endpoints.zoho.recordTourSection,
    agentId: normalizedAgentId,
    sectionId,
    sectionName: sectionName || sectionId
  }).then(res => {
    const parsed = parseZohoFunctionOutput(res);
    if (!res?.data?.details) {
      console.error('[tour] recordTourSection empty HTTP response', {
        sectionId,
        res
      });
    } else if (parsed?.ok === false) {
      console.error('[tour] recordTourSection Zoho error', {
        sectionId,
        message: parsed.message
      });
    }
    return res;
  });
}

function getSectionRoute(section, user) {
  if (section.getRoute) return section.getRoute(user);
  return section.route || null;
}

function routeMatchesPathname(section, pathname, user) {
  if (section.skipNavigation) return true;
  const route = getSectionRoute(section, user);
  if (!route) return true;
  return pathname.startsWith(route.split('?')[0]);
}

/** Auto-advance steps: highlight only, no popover chrome (title, body, buttons). */
function isInvisibleTourStep(step) {
  if (step.invisible) return true;
  if (!step.hideButtons) return false;
  const hasTitle = Boolean(step.title);
  const hasDescription =
    typeof step.description === 'function' || Boolean(step.description);
  return !hasTitle && !hasDescription;
}

function hideTourPopover(popover) {
  if (!popover?.wrapper) return;
  popover.wrapper.style.display = 'none';
  popover.wrapper.style.visibility = 'hidden';
  if (popover.arrow) popover.arrow.style.display = 'none';
}

function findPrevVisibleStepIndex(tourSteps, fromIndex) {
  for (let j = fromIndex - 1; j >= 0; j--) {
    if (!isInvisibleTourStep(tourSteps[j])) return j;
  }
  return -1;
}

function getAutoStartIntro(section) {
  if (!section) return null;
  if (section.autoStartIntro) return section.autoStartIntro;
  if (!section.route && !section.getRoute) return null;
  return {
    title: `Welcome to ${section.label}`,
    description: `We'll guide you through the <b>${section.label}</b> section step by step.`
  };
}

export function PortalWalkthroughProvider({ children }) {
  if (company !== 'oneill') return children;
  return (
    <PortalWalkthroughProviderInner>{children}</PortalWalkthroughProviderInner>
  );
}

function PortalWalkthroughProviderInner({ children }) {
  const user = useSelector(state => state.user.zohoData);
  const zohoId = user.id;
  const { targetId: viewAsAgentId } = useViewAs();
  const carriersArray = useSelector(state => state.appointments.carriersArray);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const isTourDisabled =
    isViewingAsAgent(viewAsAgentId, search) ||
    getFrontendInstance() === 'staging';
  const driverRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const autoStartTimerRef = useRef(null);
  const advanceSectionRef = useRef(null);
  const tourNavDirectionRef = useRef('forward');
  const firstTimeRedirectDoneRef = useRef(false);
  // Tracks the last pathname for which auto-start ran — prevents re-triggering
  // on the same page after a section completes (Option B behavior).
  const prevPathnameRef = useRef(null);
  const autoStartedForCurrentPathRef = useRef(false);
  const completedIdsSnapshotRef = useRef(null);
  const zohoTourSeedInitializedRef = useRef(false);
  // Tracks whether Show Demo is active so the Zoho seed effect never overwrites the reset.
  const isDemoActiveRef = useRef(false);
  // Auto-start waits until Zoho progress is resolved (or Deluge field is absent).
  const [zohoProgressReady, setZohoProgressReady] = useState(false);
  // When the page is still loading, stores the section index waiting to auto-start.
  // notifyPageReady() reads this and fires the 1.5s timer.
  const pendingAutoStartIdxRef = useRef(-1);
  const isFirstTimeRef = useRef(false);

  const dispatch = useDispatch();
  const completedIds =
    useSelector(state => state.user.completedTourSections) ?? [];

  const [isActive, setIsActive] = useState(false);
  const [isPendingCompletion, setIsPendingCompletion] = useState(false);
  const [isManualTour, setIsManualTour] = useState(false);
  const [isAtCheckpoint, setIsAtCheckpoint] = useState(false);
  const [isPageIntroCheckpoint, setIsPageIntroCheckpoint] = useState(false);
  const [pendingAutoStartPath, setPendingAutoStartPath] = useState(null);
  const [isPageReady, setIsPageReady] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const isAdmin =
    user?.Position_at_Agency === USER_TYPES.ADMIN && !!user?.Account_Name;
  const isAgentLimited = isAgentRankLimited(user?.Rank);

  const parentAppointmentGroups = useMemo(() => {
    const groups = new Set();
    carriersArray
      ?.filter(c => !!c.appointmentParentGroup)
      .forEach(c => groups.add(getParentGroupLabel(c.appointmentParentGroup)));
    return groups;
  }, [carriersArray]);

  // the by-state carrier types filter shows with 2+ types, which can include Off-Market
  const carrierTypeCount = useMemo(
    () => getCarrierTypeOptions(carriersArray).length,
    [carriersArray]
  );

  const sections = useMemo(() => {
    const flags = {
      isAdmin,
      isAgentLimited,
      parentAppointmentGroups,
      carrierTypeCount
    };
    return TOUR_SECTIONS.filter(s => {
      if (s.disabled) return false;
      if (s.adminOnly && !isAdmin) return false;
      if (s.oneillOnly && company !== 'oneill') return false;
      if (s.showFor && !s.showFor(user, flags)) return false;
      return true;
    });
  }, [
    isAdmin,
    isAgentLimited,
    user?.Customers_Tab,
    user?.NPN,
    user?.Hide_Customers_Commissions_Tab,
    user?.Hide_Lead_Partners_Tab
  ]);

  const currentSection = sections[currentIndex] || null;

  // isFirstTimeDone: true when the override flag is set in Zoho, or when all applicable
  // sections are recorded. Derived from zohoData so Show Demo resets don't affect it.
  const isFirstTimeDone =
    Boolean(user?.Override_Portal_Walkthrough_Progress) ||
    (sections.length > 0 &&
      sections
        .filter(s => s.id !== 'checklist')
        .every(s => (user?.completedTourSections || []).includes(s.id)));

  // isFirstTime: true while the agent hasn't completed all sections yet.
  // Controls checklist section clickability and X button visibility.
  const isFirstTime = !isFirstTimeDone;
  isFirstTimeRef.current = isFirstTime;

  // First-time agents are redirected to /home on login so the welcome tour starts there.
  useEffect(() => {
    if (!zohoId) return;
    if (isTourDisabled || isFirstTimeDone) return;
    if (firstTimeRedirectDoneRef.current) return;
    firstTimeRedirectDoneRef.current = true;
    if (!pathname.startsWith('/home')) navigate('/home');
  }, [zohoId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed completedIds from Zoho into Redux on page load.
  // Zoho is authoritative — dispatches fresh data so the checklist reflects real state.
  // Auto-start is gated on zohoProgressReady to prevent firing before this runs.
  useEffect(() => {
    if (!zohoId) return;

    const rawZohoSections = user?.completedTourSections;
    if (!Array.isArray(rawZohoSections)) {
      setZohoProgressReady(true);
      return;
    }

    const zohoSections = [...new Set(rawZohoSections)];

    // Skip overwrite during Show Demo — startTour() resets completedIds to [] intentionally.
    // A Zoho data refresh during navigation would otherwise restore all prior sections.
    if (
      !isDemoActiveRef.current &&
      !completedIdsEqual(completedIds, zohoSections)
    ) {
      dispatch(setCompletedTourSections(zohoSections));
      autoStartedForCurrentPathRef.current = false;
      prevPathnameRef.current = null;
      zohoTourSeedInitializedRef.current = true;
    }

    setZohoProgressReady(true);
  }, [zohoId, user?.completedTourSections]); // eslint-disable-line react-hooks/exhaustive-deps

  // View as Agent: disable tour entirely for the impersonated session
  useEffect(() => {
    if (!isTourDisabled) return;
    clearTimeout(highlightTimerRef.current);
    clearTimeout(autoStartTimerRef.current);
    driverRef.current?.destroy();
    driverRef.current = null;
    isDemoActiveRef.current = false;
    setIsActive(false);
    setIsAtCheckpoint(false);
    setIsPageReady(true);
  }, [isTourDisabled]);

  // Per-page auto-start: when the agent navigates to a page with a pending section,
  // start that section's tour after the page finishes loading.
  // - If the page is already ready (data cached): starts the 1.5s timer immediately.
  // - If the page is still loading: stores the pending section index so notifyPageReady()
  //   can fire the timer when called. A 4s fallback handles pages that never call it.
  // Does NOT re-trigger on the same page after a section completes (prevPathname guard).
  useEffect(() => {
    clearTimeout(autoStartTimerRef.current);
    pendingAutoStartIdxRef.current = -1;
    if (isTourDisabled || isFirstTimeDone || isActive || isPendingCompletion)
      return;
    if (!zohoProgressReady) return;
    if (!zohoId || sections.length === 0) return;

    // If Zoho (or advanceSection) updated completedIds while idle, allow re-auto-start.
    const completedKey = completedIds.join('\0');
    if (completedIdsSnapshotRef.current !== completedKey) {
      if (completedIdsSnapshotRef.current !== null && !isActive) {
        autoStartedForCurrentPathRef.current = false;
      }
      completedIdsSnapshotRef.current = completedKey;
    }

    // Reset the "already started" flag when the pathname changes
    if (prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
      autoStartedForCurrentPathRef.current = false;
    }

    // Only trigger once per page visit
    if (autoStartedForCurrentPathRef.current) return;

    // matchesPage() reads the DOM and can throw if elements aren't mounted yet.
    // For auto-start, route matching is sufficient — the first pending section on
    // this page wins regardless of which tab is currently active.
    const sectionIdx = sections.findIndex(
      s =>
        s.id !== 'checklist' &&
        !completedIds.includes(s.id) &&
        routeMatchesPathname(s, pathname, user)
    );
    if (sectionIdx < 0) return;

    autoStartedForCurrentPathRef.current = true;

    const fireAutoStart = idx => {
      const section = sections[idx];
      const intro = isFirstTime ? getAutoStartIntro(section) : null;
      pendingAutoStartIdxRef.current = -1;
      setPendingAutoStartPath(null);
      clearTimeout(autoStartTimerRef.current);
      autoStartTimerRef.current = setTimeout(() => {
        setIsManualTour(false);
        setCurrentIndex(idx);
        if (intro) {
          setIsPageIntroCheckpoint(true);
          setIsAtCheckpoint(true);
        } else {
          setIsPageIntroCheckpoint(false);
          setIsAtCheckpoint(false);
        }
        setIsActive(true);
      }, 1500);
    };

    if (isPageReady) {
      fireAutoStart(sectionIdx);
    } else {
      pendingAutoStartIdxRef.current = sectionIdx;
      setPendingAutoStartPath(pathname.split('?')[0]);
      const fallbackMs = isFirstTime ? 8000 : 4000;
      autoStartTimerRef.current = setTimeout(
        () => fireAutoStart(sectionIdx),
        fallbackMs
      );
    }

    return () => {
      clearTimeout(autoStartTimerRef.current);
      pendingAutoStartIdxRef.current = -1;
    };
  }, [
    pathname,
    isFirstTime,
    isFirstTimeDone,
    isActive,
    zohoId,
    completedIds,
    zohoProgressReady,
    isPendingCompletion,
    sections
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset page-ready when a manual-tour checkpoint lands on a page that needs to load data
  useEffect(() => {
    if (isTourDisabled) return;
    if (!isActive || !isAtCheckpoint || !currentSection) return;
    if (currentSection.skipNavigation) return;
    setIsPageReady(false);
  }, [isActive, isAtCheckpoint, currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety net if a page never calls notifyPageReady (e.g. missing hook on a new route)
  useEffect(() => {
    if (!isActive || isPageReady) return;
    const fallbackTimer = setTimeout(() => setIsPageReady(true), 3000);
    return () => clearTimeout(fallbackTimer);
  }, [isActive, isPageReady, currentIndex]);

  // Pages with async data call this when their content is ready.
  // Also triggers a pending auto-start timer if one is waiting for this page to load.
  const notifyPageReady = useCallback(() => {
    setIsPageReady(true);
    const pendingIdx = pendingAutoStartIdxRef.current;
    if (pendingIdx < 0) return;
    const section = sections[pendingIdx];
    const intro = isFirstTimeRef.current ? getAutoStartIntro(section) : null;
    pendingAutoStartIdxRef.current = -1;
    setPendingAutoStartPath(null);
    clearTimeout(autoStartTimerRef.current);
    autoStartTimerRef.current = setTimeout(() => {
      setIsManualTour(false);
      setCurrentIndex(pendingIdx);
      if (intro) {
        setIsPageIntroCheckpoint(true);
        setIsAtCheckpoint(true);
      } else {
        setIsPageIntroCheckpoint(false);
        setIsAtCheckpoint(false);
      }
      setIsActive(true);
    }, 1500);
  }, [sections]);

  // Navigate to the current section's route when currentIndex advances (manual tour only)
  useEffect(() => {
    if (!isActive || !isManualTour || !currentSection) return;
    if (isAtCheckpoint) return; // checkpoint navigates via advanceSection
    if (currentSection.skipNavigation) return;

    const targetRoute = getSectionRoute(currentSection, user);
    if (!targetRoute) return;

    const normalizedTarget = targetRoute.split('?')[0];
    if (!pathname.startsWith(normalizedTarget)) {
      navigate(targetRoute);
    }
  }, [isActive, currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire Driver.js — checkpoint spotlight (manual tour) OR section tour (both modes)
  useEffect(() => {
    if (isTourDisabled) return;
    if (!isActive || !currentSection) return;

    clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => {
      // If a section tour is actively running, a pathname change came from within
      // a tour step — don't destroy or restart the driver.
      if (driverRef.current && !isAtCheckpoint) return;

      driverRef.current?.destroy();

      // Injects a subtle "Close Tour" button at the bottom of any popover (manual tour only).
      // Reloads the page on click so the agent lands on a clean state.
      const injectCloseTourBtn = popover => {
        if (!isManualTour || !popover?.wrapper) return;
        if (popover.wrapper.querySelector('.driver-close-tour-btn')) return;
        const btn = document.createElement('button');
        btn.className = 'driver-close-tour-btn';
        btn.textContent = '✕ Close Tour';
        btn.style.cssText =
          'display:block;width:100%;background:none;border:none;' +
          'margin-top:8px;border-top:1px solid rgba(0,0,0,0.07);padding:7px 16px;' +
          'font-size:11px;color:#bbb;cursor:pointer;text-align:center;' +
          'letter-spacing:0.2px;';
        btn.addEventListener('mouseenter', () => {
          btn.style.color = '#777';
        });
        btn.addEventListener('mouseleave', () => {
          btn.style.color = '#bbb';
        });
        btn.addEventListener('click', () => {
          driverRef.current?.destroy();
          driverRef.current = null;
          window.location.reload();
        });
        popover.wrapper.appendChild(btn);
      };

      // ── CHECKPOINT MODE ───────────────────────────────────────────────────
      // Manual tour: checklist panel + "Start →". First-time auto-start: page intro.
      if (isAtCheckpoint) {
        if (!isPageReady) return;

        // First-time auto-start: centered "Welcome to this page" before section steps.
        if (isPageIntroCheckpoint) {
          const intro = getAutoStartIntro(currentSection);
          const handleStartPageIntro = () => {
            driverRef.current?.destroy();
            driverRef.current = null;
            clearTimeout(highlightTimerRef.current);
            setIsPageIntroCheckpoint(false);
            setIsAtCheckpoint(false);
          };

          const driverInstance = driver({
            allowClose: false,
            animate: true,
            smoothScroll: true,
            overlayOpacity: 0.45,
            stagePadding: 8,
            stageRadius: 12,
            popoverClass: 'oneill-tour-popover',
            steps: [
              {
                popover: {
                  title: intro?.title || `Welcome to ${currentSection.label}`,
                  description:
                    intro?.description ||
                    `We'll guide you through the ${currentSection.label} section.`,
                  side: 'bottom',
                  align: 'center',
                  nextBtnText: 'Start',
                  showButtons: ['next'],
                  onNextClick: handleStartPageIntro
                }
              }
            ]
          });

          driverRef.current = driverInstance;
          driverInstance.drive();
          return;
        }

        const isIntro = currentSection.id === 'checklist';
        const isLastSection = !sections.some(
          s =>
            s.id !== 'checklist' &&
            s.id !== currentSection.id &&
            !completedIds.includes(s.id)
        );

        const checkpointDescription = Array.isArray(currentSection.steps)
          ? `Follow a guided tour through the ${currentSection.label} section.`
          : currentSection.description ||
            `Explore the ${currentSection.label} section.`;

        const handleStart = () => {
          driverRef.current?.destroy();
          driverRef.current = null;
          clearTimeout(highlightTimerRef.current);

          if (isIntro) {
            const nextIdx = currentIndex + 1;
            if (nextIdx < sections.length) {
              setCurrentIndex(nextIdx);
              setIsAtCheckpoint(false);
            } else {
              setIsActive(false);
              setIsAtCheckpoint(false);
            }
          } else {
            setIsAtCheckpoint(false);
          }
        };

        // Previous Section: only in manual tour, based on the last completed section ID
        // in the completedIds array (most recently completed = last element).
        let prevSectionIdx = -1;
        let isPrevSectionSamePage = false;
        if (isManualTour && !isIntro && completedIds.length > 0) {
          const lastCompletedId = completedIds[completedIds.length - 1];
          prevSectionIdx = sections.findIndex(s => s.id === lastCompletedId);
          if (prevSectionIdx >= 0) {
            const prevRoute = getSectionRoute(sections[prevSectionIdx], user);
            isPrevSectionSamePage = prevRoute
              ? pathname.startsWith(prevRoute.split('?')[0])
              : false;
          }
        }

        const handlePrev = () => {
          driverRef.current?.destroy();
          driverRef.current = null;
          clearTimeout(highlightTimerRef.current);
          if (prevSectionIdx < 0) return;
          const prevSection = sections[prevSectionIdx];
          setCurrentIndex(prevSectionIdx);
          if (prevSection && !prevSection.skipNavigation) {
            const route = getSectionRoute(prevSection, user);
            if (route) navigate(route);
          }
          setIsAtCheckpoint(true);
        };

        const driverInstance = driver({
          allowClose: false,
          animate: true,
          smoothScroll: true,
          overlayOpacity: 0.45,
          stagePadding: 8,
          stageRadius: 12,
          popoverClass: 'oneill-tour-popover',
          prevBtnText: 'Previous Section',
          steps: [
            {
              element: '[data-tour="checklist-panel"]',
              popover: {
                title: isIntro
                  ? 'Welcome to the Portal Tour!'
                  : `Next Up: ${currentSection.label}`,
                description: isIntro
                  ? 'This checklist guides you through each section of the portal. Complete all sections to unlock everything!'
                  : `${checkpointDescription}<br><br><span style="font-size:11px;opacity:0.65;">📝 Note: You can jump to any section at any time by clicking its name in the checklist.</span>`,
                side: 'left',
                align: 'center',
                nextBtnText: isIntro
                  ? 'Start Tour'
                  : isLastSection
                  ? 'Start Last Section'
                  : 'Start',
                showButtons:
                  isIntro || prevSectionIdx < 0
                    ? ['next']
                    : ['next', 'previous'],
                onNextClick: handleStart,
                ...(!isIntro && prevSectionIdx >= 0
                  ? { onPrevClick: handlePrev }
                  : {}),
                onPopoverRender: popover => {
                  // Re-enable prev button on step 0 when previous section is a different page
                  if (
                    !isIntro &&
                    prevSectionIdx >= 0 &&
                    popover.previousButton &&
                    !isPrevSectionSamePage
                  ) {
                    popover.previousButton.removeAttribute('disabled');
                    popover.previousButton.style.pointerEvents = 'auto';
                    popover.previousButton.style.opacity = '1';
                  }
                  injectCloseTourBtn(popover);
                }
              }
            }
          ]
        });

        driverRef.current = driverInstance;
        driverInstance.drive();
        return;
      }

      // ── SECTION TOUR MODE ─────────────────────────────────────────────────
      if (currentSection.id === 'checklist') return;
      if (!routeMatchesPathname(currentSection, pathname, user)) return;

      const isMobile = window.innerWidth < 768;
      // For manual tour: show "Next Section" / "Finish Tour ✓" based on remaining sections.
      // For auto-start: always show "Finish ✓" — agent stays on the page after completing.
      const isLastSection = isManualTour
        ? !sections.some(
            s =>
              s.id !== 'checklist' &&
              s.id !== currentSection.id &&
              !completedIds.includes(s.id)
          )
        : false;
      const isMultiStep =
        Array.isArray(currentSection.steps) && currentSection.steps.length > 0;

      let steps;
      if (isMultiStep) {
        const visibleSteps = currentSection.steps.filter(
          step => !step.stepShowFor || step.stepShowFor(user)
        );
        const stepFlags = {
          isAdmin,
          isAgentLimited,
          parentAppointmentGroups,
          carrierTypeCount
        };
        tourNavDirectionRef.current = 'forward';
        steps = visibleSteps.map((step, i) => {
          const isLastSubStep = i === visibleSteps.length - 1;
          const invisible = isInvisibleTourStep(step);
          const prevVisibleIdx = findPrevVisibleStepIndex(visibleSteps, i);
          const description =
            typeof step.description === 'function'
              ? step.description(user, stepFlags)
              : step.description;
          const showButtons = invisible
            ? []
            : step.hideButtons
            ? []
            : step.showButtons ?? ['next', 'previous'];
          const hasPreviousButton = showButtons.includes('previous');

          // Function selectors are passed through so Driver.js evaluates them when
          // each step is highlighted (required for late-resolved targets, e.g. Webinars tab).
          const resolvedElement =
            typeof step.selector === 'function' ? step.selector : step.selector;
          return {
            element: !isMobile ? resolvedElement : undefined,
            scrollIntoViewOptions: step.scrollIntoViewOptions ?? undefined,
            onHighlighted: step.onHighlighted
              ? (el, stepObj) => {
                  if (invisible && tourNavDirectionRef.current === 'backward') {
                    tourNavDirectionRef.current = 'forward';
                    return;
                  }
                  if (!invisible) tourNavDirectionRef.current = 'forward';
                  step.onHighlighted(el, stepObj, driverRef.current);
                }
              : !invisible
              ? () => {
                  tourNavDirectionRef.current = 'forward';
                }
              : undefined,
            popover: {
              title: invisible ? undefined : step.title,
              description: invisible ? undefined : description,
              side: step.side || 'right',
              align: step.align || 'start',
              nextBtnText: isLastSubStep
                ? isManualTour
                  ? isLastSection
                    ? 'Finish Tour ✓'
                    : 'Next Section'
                  : 'Finish ✓'
                : 'Next',
              showButtons,
              ...(invisible
                ? {
                    popoverClass: 'oneill-tour-popover--highlight-only',
                    onPopoverRender: popover => {
                      hideTourPopover(popover);
                      step.onPopoverRender?.(popover);
                    }
                  }
                : {
                    onPopoverRender: popover => {
                      step.onPopoverRender?.(popover);
                      injectCloseTourBtn(popover);
                    }
                  }),
              ...(hasPreviousButton && prevVisibleIdx >= 0
                ? {
                    onPrevClick: step.onPrevClick
                      ? (el, stepObj) => {
                          tourNavDirectionRef.current = 'backward';
                          step.onPrevClick(el, stepObj, {
                            driver: driverRef.current
                          });
                        }
                      : () => {
                          tourNavDirectionRef.current = 'backward';
                          driverRef.current?.moveTo(prevVisibleIdx);
                        }
                  }
                : {}),
              ...(isLastSubStep || step.closeOnNext
                ? {
                    onNextClick: () => {
                      tourNavDirectionRef.current = 'forward';
                      step.closeOnNext?.();
                      if (isLastSubStep) {
                        driverRef.current?.destroy();
                        advanceSectionRef.current();
                      } else {
                        driverRef.current?.moveNext();
                      }
                    }
                  }
                : {})
            }
          };
        });
      } else {
        const el = document.querySelector(currentSection.selector);
        steps = [
          {
            element: !isMobile && el ? currentSection.selector : undefined,
            popover: {
              title: currentSection.title || currentSection.label,
              description: currentSection.description,
              side: currentSection.side || 'right',
              align: currentSection.align || 'start',
              nextBtnText: isManualTour
                ? isLastSection
                  ? 'Finish Tour ✓'
                  : 'Next Section'
                : 'Finish ✓',
              showButtons: ['next'],
              onNextClick: () => {
                advanceSectionRef.current();
              },
              onPopoverRender: popover => {
                injectCloseTourBtn(popover);
              }
            }
          }
        ];
      }

      const driverInstance = driver({
        allowClose: false,
        animate: true,
        smoothScroll: true,
        overlayOpacity: 0.45,
        stagePadding: 6,
        stageRadius: 8,
        popoverClass: 'oneill-tour-popover',
        prevBtnText: 'Previous',
        steps
      });

      driverRef.current = driverInstance;
      if (currentSection.setup) {
        const extraDelay = currentSection.setup() || 0;
        setTimeout(() => driverInstance.drive(), 500 + extraDelay);
      } else {
        driverInstance.drive();
      }
    }, 350);

    return () => clearTimeout(highlightTimerRef.current);
  }, [
    isActive,
    currentIndex,
    pathname,
    isAtCheckpoint,
    isPageIntroCheckpoint,
    isPageReady,
    isManualTour
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const advanceSection = useCallback(() => {
    if (isTourDisabled) return;
    const section = sections[currentIndex];
    if (!section) return;

    // Dedup guard: skip if already recorded. In manual/demo mode this is bypassed
    // because no Zoho recording happens and the user can freely redo sections.
    if (!isManualTour && completedIds.includes(section.id)) return;

    const newCompleted = completedIds.includes(section.id)
      ? completedIds
      : [...completedIds, section.id];
    dispatch(setCompletedTourSections(newCompleted));

    driverRef.current?.destroy();
    driverRef.current = null;

    // Find the next uncompleted section
    let nextIndex = sections.findIndex(
      (s, i) =>
        i > currentIndex && s.id !== 'checklist' && !newCompleted.includes(s.id)
    );
    if (nextIndex < 0) {
      nextIndex = sections.findIndex(
        (s, i) =>
          i <= currentIndex &&
          s.id !== 'checklist' &&
          !newCompleted.includes(s.id)
      );
    }
    const isAllDone = nextIndex < 0;

    const agentId = zohoId || user?.id;

    if (!isManualTour) {
      // First-time auto-start: call the endpoint inline, show overlay, then update
      // completedIds from Zoho's fresh completedSections response. No page reload needed.
      setIsActive(false);
      setIsAtCheckpoint(false);

      // Sync progress to Zoho even when Override_Portal_Walkthrough_Progress is set.
      // shouldSyncTourSectionToZoho still dedups against Redux + Zoho state.
      if (shouldSyncTourSectionToZoho(user, section.id, completedIds)) {
        setIsPendingCompletion(true);

        recordTourSectionToZoho(agentId, section.id, section.label)
          .then(res => {
            const parsed = parseZohoFunctionOutput(res);
            if (Array.isArray(parsed?.completedSections)) {
              dispatch(
                setCompletedTourSections(
                  parsed.completedSections.filter(Boolean)
                )
              );
            }
            setTimeout(() => setIsPendingCompletion(false), 500);
          })
          .catch(err => {
            console.error('[tour] recordTourSection failed', {
              sectionId: section.id,
              err
            });
            setIsPendingCompletion(false);
          });
      }

      return;
    }

    // Manual tour: record immediately and navigate.
    // Sync progress to Zoho even when Override_Portal_Walkthrough_Progress is set.
    if (shouldSyncTourSectionToZoho(user, section.id, completedIds)) {
      recordTourSectionToZoho(agentId, section.id, section.label)
        .then(res => {
          const parsed = parseZohoFunctionOutput(res);
          if (Array.isArray(parsed?.completedSections)) {
            dispatch(
              setCompletedTourSections(parsed.completedSections.filter(Boolean))
            );
          }
        })
        .catch(err =>
          console.error('[tour] recordTourSection failed', {
            sectionId: section.id,
            err
          })
        );
    }

    if (isAllDone) {
      isDemoActiveRef.current = false;
      setIsActive(false);
      setIsAtCheckpoint(false);
      navigate('/home');
    } else if (isManualTour) {
      // Manual tour: navigate to next section with checkpoint
      setCurrentIndex(nextIndex);
      const nextSection = sections[nextIndex];
      if (!nextSection.skipNavigation) {
        const nextRoute = getSectionRoute(nextSection, user);
        if (nextRoute) navigate(nextRoute);
      }
      setIsAtCheckpoint(true);
    } else {
      // Auto-start: reset the per-page guard so the next pending section on the
      // same route (e.g. another pending section on /home) can auto-start immediately.
      autoStartedForCurrentPathRef.current = false;
      setIsActive(false);
      setIsAtCheckpoint(false);
    }
  }, [
    currentIndex,
    completedIds,
    sections,
    navigate,
    user,
    zohoId,
    isManualTour,
    isTourDisabled,
    isFirstTimeDone
  ]); // eslint-disable-line react-hooks/exhaustive-deps
  advanceSectionRef.current = advanceSection;

  // Start (or re-trigger) the tour manually — begins at the intro checkpoint.
  // Only available once isFirstTimeDone is true.
  const startTour = useCallback(
    (fromSectionId = null) => {
      if (isTourDisabled) return;
      driverRef.current?.destroy();
      driverRef.current = null;
      clearTimeout(highlightTimerRef.current);
      clearTimeout(autoStartTimerRef.current);
      isDemoActiveRef.current = true;

      const idx = fromSectionId
        ? Math.max(
            0,
            sections.findIndex(s => s.id === fromSectionId)
          )
        : 0;

      dispatch(setCompletedTourSections([]));
      setCurrentIndex(idx);
      setIsManualTour(true);
      setIsAtCheckpoint(true);
      setIsActive(true);

      const section = sections[idx];
      if (section && !section.skipNavigation) {
        const route = getSectionRoute(section, user);
        if (route) {
          const normalizedTarget = route.split('?')[0];
          // Also navigate when the section route has no query params but the current
          // URL does — e.g. /carrier-appointments?carrier=Ambetter must reset to /carrier-appointments
          // so the carrier detail view is cleared before the tour starts.
          const needsQueryReset = !route.includes('?') && !!search;
          if (!pathname.startsWith(normalizedTarget) || needsQueryReset) {
            navigate(route);
          }
        }
      }
    },
    [sections, navigate, user, pathname, search, isTourDisabled]
  );

  // Jump to a specific section (only allowed after first-time is done)
  const goToSection = useCallback(
    sectionId => {
      if (isTourDisabled || isFirstTime) return;
      const idx = sections.findIndex(s => s.id === sectionId);
      if (idx < 0) return;

      driverRef.current?.destroy();
      driverRef.current = null;
      clearTimeout(highlightTimerRef.current);
      clearTimeout(autoStartTimerRef.current);

      setIsManualTour(true);
      setCurrentIndex(idx);
      setIsAtCheckpoint(true);
      const section = sections[idx];
      if (section && !section.skipNavigation) {
        const route = getSectionRoute(section, user);
        if (route) navigate(route);
      }
    },
    [isTourDisabled, isFirstTime, sections, navigate, user]
  );

  const endTour = useCallback(() => {
    if (isTourDisabled) return;
    driverRef.current?.destroy();
    driverRef.current = null;
    clearTimeout(highlightTimerRef.current);
    clearTimeout(autoStartTimerRef.current);
    isDemoActiveRef.current = false;
    setIsActive(false);
    setIsAtCheckpoint(false);
    setIsPageIntroCheckpoint(false);
    setPendingAutoStartPath(null);
  }, [isTourDisabled]);

  // Destroy driver on unmount
  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
      clearTimeout(highlightTimerRef.current);
      clearTimeout(autoStartTimerRef.current);
    };
  }, []);

  const value = {
    isTourDisabled,
    isActive: isTourDisabled ? false : isActive,
    isPendingCompletion,
    isFirstTime,
    isFirstTimeDone,
    isAtCheckpoint,
    isPageIntroCheckpoint,
    isPageReady,
    pendingAutoStartPath,
    completedIds,
    currentIndex,
    sections,
    currentSectionId: currentSection?.id || null,
    currentSection,
    startTour,
    goToSection,
    endTour,
    advanceSection,
    notifyPageReady
  };

  return (
    <PortalWalkthroughContext.Provider value={value}>
      {children}
    </PortalWalkthroughContext.Provider>
  );
}

PortalWalkthroughProvider.propTypes = {
  children: PropTypes.node.isRequired
};

PortalWalkthroughProviderInner.propTypes = {
  children: PropTypes.node.isRequired
};
